import { AttachmentParentType, AttachmentType } from '../attachment/attachment';
import { IUser } from '../auth/auth';
import { IPartnerData } from '../partner/partner';
import type { IBitPublic } from '../bit/bit.d';

export enum IPostAttachmentTypes {
  IMAGE = 'image',
  MODEL = 'model',
  YOUTUBE = 'youtube',
  ZIP = 'zip',
}

export enum IAttachmentCategory {
  postMedia = 'postMedia',
  postModel = 'postModel',
  postThumbnail = 'postThumbnail',
  postZip = 'postZip',
  // Backend-generated bundle consumed only by the Redline HMI — never shown in
  // the web UI (see filterAttachments).
  redlineHmiProjectZip = 'redlineHmiProjectZip',
}

export enum IAttachmentParentType {
  POST = 'post',
}

export enum ILicense {
  CreativeCommonsPublicDomain = 'Creative Commons Public Domain',
  CreativeCommonsAttribution = 'Creative Commons Attribution',
  CreativeCommonsAttributionShareAlike = 'Creative Commons Attribution - Share Alike',
  CreativeCommonsAttributionNoDerivatives = 'Creative Commons Attribution - No Derivatives',
  CreativeCommonsAttributionNoncommercial = 'Creative Commons Attribution - Noncommercial',
  CreativeCommonsAttributionNoncommercialShareAlike = 'Creative Commons Attribution - Noncommercial - Share Alike',
  CreativeCommonsAttributionNoncommercialNoDerivatives = 'Creative Commons Attribution - Noncommercial - No Derivatives',
}

export interface IPostAttachment {
  _id: string;
  parentId: string;
  parentType: AttachmentParentType;
  fileName: string;
  user: string;
  resourcePath: string;
  url: string;
  fileSize: number;
  fileHash: string;
  resourceType: AttachmentType;
  category: IAttachmentCategory;
  downloadCount: number;
  createdAt: string;
  updatedAt: string;
  controller?: string;
}

export interface IPostCategory {
  _id: string;
  name: string;
  type: string;
  createdAt: string;
}

export interface IPostSubCategory {
  _id: string;
  name: string;
  createdAt: string;
}

export type IPostTag = {
  _id: string;
  name: string;
  type: string;
  createdBy: string;
  createdAt: string;
};

export enum IPostVisibility {
  PUBLIC = 'public',
  PRIVATE = 'private',
  UNLISTED = 'unlisted',
  DRAFT = 'draft',
  REJECTED_WITH_COMMENTS = 'rejected_with_comments',
}

export enum IBuiltWith {
  ONEFINITY = 'Onefinity CNC',
  OTHERS = 'Others',
}

export enum IOnefinityModel {
  ELITE_SERIES = 'Onefinity Elite Series',
  PRO_SERIES = 'Onefinity Pro Series',
  ORIGINAL_SERIES = 'Onefinity Original Series',
}

export enum IOnefinityController {
  MASSO = 'Masso',
  BB = 'Build Botics',
}

export enum IControllerType {
  REDLINE = 'Redline',
  MASSO = 'Masso',
  BUILDOTICS = 'Buildbotics',
  OTHER = 'Other',
}

export enum IOnefinitySize {
  FOREMAN_48X48 = 'Foreman 48x48',
  JOURNEYMAN_48X32 = 'Journeyman 48x32',
  WOODWORKER_32X32 = 'Woodworker 32x32',
  MACHINIST_16X16 = 'Machinist 16x16',
}

export enum ISoftwareUsed {
  CARVECO = 'Carveco',
  VECTRIC = 'Vectric',
  FUSION_360 = 'Fusion 360',
  CARBIDE_CREATE = 'Carbide Create',
  EASEL = 'Easel',
  SOLIDWORKS = 'Solidworks',
  MESHCAM = 'MeshCam',
  EZ_CAD = 'EZ Cad',
  FREECAM = 'FreeCAM',
  ESTLCAM = 'Estlcam',
  LIGHTBURN = 'LightBurn',
  OTHER = 'Other',
}

export enum IWood {
  WOOD_HARD = 'Wood (hard)',
  WOOD_SOFT = 'Wood (soft)',
  PLYWOOD = 'Plywood',
  MDF = 'MDF',
}
export enum IMetals {
  ALUMINUM = 'Aluminum',
  BRASS = 'Brass',
  COPPER = 'Copper',
}
export enum IPlastic {
  PLASTIC = 'Plastic',
  ACRYLIC = 'Acrylic',
  HDPE = 'HDPE',
}
export enum IOther {
  EVA = 'EVA',
  LEATHER = 'Leather',
  GLASS = 'Glass',
  FOAM = 'Foam',
}

export enum IMaterialUsed {
  WOOD = IWood,
  METALS = IMetals,
  PLASTIC = IPlastic,
  OTHER = IOther,
}

export enum IDifficulty {
  BEGINNER = 'Beginner',
  ADVANCED = 'Advanced',
  EXPERT = 'Expert',
}

export enum IJobType {
  SINGLE_SIDED = 'Single Sided',
  DOUBLE_SIDED = 'Double Sided',
  ROTARY = 'Rotary',
  TILE = 'Tile',
}

export enum IFlipDirection {
  LEFT_TO_RIGHT = 'Left to Right',
  FRONT_TO_BACK = 'Front to Back',
}

export enum IUnits {
  INCHES = 'Inches',
  MM = 'MM',
}

export enum IZZeroPosition {
  MATERIAL_SURFACE = 'Material Surface',
  MACHINE_BED = 'Machine Bed',
  CYLINDER_SURFACE = 'Cylinder Surface',
  CYLINDER_AXIS = 'Cylinder Axis',
}

export enum IXYDatumPosition {
  LOWER_LEFT = 'Lower Left',
  CENTER = 'Center',
  UPPER_LEFT = 'Upper Left',
  LOWER_RIGHT = 'Lower Right',
  UPPER_RIGHT = 'Upper Right',
}

export enum ILaserOrEndmill {
  ENDMILL = 'Endmill',
  LASER = 'Laser',
}

export enum IBitType {
  UPCUT_ENDMILL = 'Upcut Endmill',
  DOWNCUT_ENDMILL = 'Downcut Endmill',
  COMPRESSION_BIT = 'Compression Bit',
  V_BIT = 'V Bit',
  TAPERED_BALL_NOSE = 'Tapered Ball Nose',
  BOWL = 'Bowl',
  OGEE_ROUNDOVER = 'Ogee / Roundover',
  BALL_NOSE = 'Ball Nose',
  SURFACING = 'Surfacing',
  DRAG_BIT = 'Drag Bit',
  DRILL_MILL = 'Drill Mill',
  DOVETAIL = 'Dovetail',
}

export enum IBitUsedUnit {
  INCHES = 'Inches',
  MM = 'MM',
}

export enum IToolSetter {
  ENABLED = 'Enabled',
  DISABLED = 'Disabled',
}

export enum IProbeForZHeightCommand {
  NO = 'No',
  YES = 'Yes',
}

export type IToolData = {
  modelId: string;
  // Catalog bit reference (Stage 2). Required for Endmill; for Laser it
  // references the selected laser (optional on legacy rows).
  bitId?: string;
  // Which of the bit's brands was picked (resolved against bit.brands on read).
  brandId?: string;
  // Resolved bit, attached by the backend on READ (dual-read).
  bit?: IBitPublic;
  // Legacy inline specs retained for dual-read of un-backfilled posts.
  toolUsed?: number;
  toolUnit?: IBitUsedUnit;
  toolType?: IBitType;
  laserOrEndmill: ILaserOrEndmill;
  vBitDegree?: number; // Required for V-Bit (e.g., 30, 60, 90)
  tipRadius?: number; // Required for Bowl, Ogee/Roundover bits (e.g., 0.1875, 0.25, 0.3125)
  spindleSpeed?: number; // Spindle speed in RPM (6000-24000)
  _id: string;
};

export type IModelGroup = {
  _id: string;
  name: string;
  modelIds: string[];
  toolData: IToolData[];
  createdAt: string;
};

export type IJobSize = {
  width: number;
  height: number;
  thickness: number;
  diameter: number;
  length: number;
};

export interface IModel {
  id: string;
  modelURL: string;
  modelFile: File;
}

export type IPost = {
  _id: string;
  title: string;
  description: string;
  user: string;
  category: IPostCategory;
  models: IModel[];
  subCategory: IPostSubCategory | string;
  tags: IPostTag[] | [];
  visibility: IPostVisibility;
  onefinityModel: IOnefinityModel;
  onefinityController: IOnefinityController;
  onefinitySize: IOnefinitySize;

  softwareUsed: ISoftwareUsed[];
  materialUsed: IMaterialUsed;

  jobType: IJobType;
  jobSize: {
    width: number;
    height: number;
    thickness: number;
  };
  units: IUnits;
  zZeroPosition: IZZeroPosition;
  xyDatumPosition: IXYDatumPosition;
  laserOrEndmill?: ILaserOrEndmill;

  modelGroups?: IModelGroup[];

  toolSetter: IToolSetter;
  probeForZHeightCommand: IProbeForZHeightCommand;
  isDimensionalMaterial?: boolean;
  dimensionalMaterialLinks?: string[];
  createdAt: string;
  updatedAt?: string;
  likes: number;
  comments: number;
  hasLikedByUser?: boolean;
  hasBookmarkedByUser?: boolean;
  averageTotalRating?: number;
  totalMakes?: number;

  // Featured posts fields
  isFeatured?: boolean;
  featuredAt?: string;
  featuredOrder?: number;
  rankingScore?: number;

  // Project group membership
  projectGroupId?: string | null;
  projectGroup?: {
    _id: string;
    name: string;
  } | null;

  partner?: IPartnerData;
  adminComments?: Array<{
    _id: string;
    comment: string;
    commentType: 'admin_comment';
    user: {
      _id: string;
      username: string;
      firstname?: string;
      lastname?: string;
      email: string;
      profilePicture?: string;
    };
    post: string;
    commentedAt: string;
    updatedAt?: string;
  }>;
};

export interface IPostWithAttachments extends IPost {
  attachments: IPostAttachment[];
  averageTotalRating?: number;
  totalMakes?: number;
}

export interface IPostWithUser extends IPost {
  user: IUser;
  averageTotalRating?: number;
  totalMakes?: number;
}

export interface IPostWithUserAndAttachments extends IPost {
  user: IUser;
  // Slim list endpoints no longer ship the full attachments array;
  // they expose a flat thumbnail URL and a pre-summed download count.
  attachments?: IPostAttachment[];
  postThumbnail?: string | null;
  downloadCount?: number;
  difficulty?: string;
  averageTotalRating?: number;
  totalMakes?: number;
  /** First youtube attachment / first dimensional-material link, for the card icons. */
  youtubeUrl?: string | null;
  materialLink?: string | null;
  materialLinks?: string[];
}

export interface IIPostWithUserAndAttachmentsRequestParams {
  category?: string;
  subCategory?: string;
  postedAt?: string;
  author?: string;
  page?: number;
  limit?: number;
  toolType?: string;
  searchQuery?: string;
  searchMode?: 'title' | 'user';
  partner?: string;
  ratings?: string;
  sortBy?: string;
  sortOrder?: string;
  contentType?: string;
  softwareUsed?: string;
  controllerType?: string;
}

/**
 * Sort options accepted by the unified profile feed endpoint.
 * Mirrors the backend contract: `<field>:<direction>`.
 */
export type IPostsSort = 'updatedAt:desc' | 'createdAt:desc';

export type EnumType = Record<string, string>;
export type GroupedEnum = Record<string, EnumType>;

export interface IOption {
  value: string;
  label: string;
}

export interface IGroupedOption {
  label: string;
  options: Option[];
}

export interface IPostDraft {
  user: string;
  tags: string[];
  category: string;
  difficulty: string;
  title: string;
  description: string;
  thumbnail: IFileUploadQueue;
  visibility: IPostVisibility;
  _id: string;
}

export type IPostDetailResponse = {
  _id: string;
  title: string;
  downloadCount?: number;
  description: string;
  user: IUser;
  category: IPostCategory;
  tags: IPostTag[];
  visibility: string;
  onefinityModel: string;
  onefinityController: string;
  onefinitySize: string;
  softwareUsed: string[];
  materialUsed: string;
  difficulty?: string;
  jobType: string;
  flipDirection?: string;
  jobSize: IJobSize;
  units: string;
  zZeroPosition: string;
  xyDatumPosition: string;
  laserOrEndmill?: string;
  toolSetter: string;
  probeForZHeightCommand: string;
  isDimensionalMaterial?: boolean;
  dimensionalMaterialLinks?: string[];
  materialLinkHosts?: { link: string; host: string }[];
  createdAt: string;
  updatedAt?: string;
  modelGroups?: IModelGroup[];
  likes: number;
  hasBookmarkedByUser: boolean;
  hasLikedByUser: boolean;
  comments: number;
  currentPage: number;
  builtWith: IBuiltWith;
  attachments: IPostAttachment[];
  originalMachine: string;
  supportedMachines: string[];
  licenceType: string;
  averageTotalRating?: number;
  totalMakes?: number;
  projectGroup?: {
    _id: string;
    name: string;
  } | null;
  adminComments?: Array<{
    _id: string;
    comment: string;
    commentType: 'admin_comment';
    user: {
      _id: string;
      username: string;
      firstname?: string;
      lastname?: string;
      email: string;
      profilePicture?: string;
    };
    post: string;
    commentedAt: string;
    updatedAt?: string;
  }>;
};

export type IPostUploadData = {
  fileUploadQueue: IFileUploadQueue[];
  _id: string;
  title: string;
  description: string;
  category: string;
  tags: string[];
  onefinityModel: IOnefinityModel | '';
  onefinityController: IOnefinityController | '';
  onefinitySize: IOnefinitySize | '';
  softwareUsed: ISoftwareUsed[];
  materialUsed: IMaterialUsed;
  difficulty: string;
  originalMachine: string;
  supportedMachines: string[];
  jobType: IJobType;
  flipDirection: IFlipDirection | '';
  units: IUnits;
  jobSizeHeight?: number;
  jobSizeThickness?: number;
  jobSizeWidth?: number;
  jobSizeDiameter?: number;
  jobSizeLength?: number;
  zZeroPosition: IZZeroPosition;
  xyDatumPosition: IXYDatumPosition;
  laserOrEndmill?: ILaserOrEndmill;
  modelGroups?: IModelGroup[];
  toolSetter: IToolSetter;
  probeForZHeightCommand: IProbeForZHeightCommand;
  isDimensionalMaterial: boolean;
  dimensionalMaterialLinks: string[];
  models: IPostAttachment[];
  mediaFiles: IPostAttachment[];
  youtubeAttachments: string[];
  licenceType: ILicense;
  thumbnail: IPostAttachment | null;
};

export type IPostAttachmentUploadRequest = {
  thumbnail?: File;
  modelFiles?: File[];
  mediaFiles?: File[];
};

export type IPostDataUploadRequest = {
  title: string;
  description: string;
  category: string;
  tags: string[];
  onefinityModel: string;
  onefinityController: string;
  onefinitySize: string;
  softwareUsed: string[];
  materialUsed: string;
  difficulty: string;
  jobType: string;
  jobSizeHeight: number;
  jobSizeThickness: number;
  jobSizeWidth: number;
  jobSizeDiameter: number;
  jobSizeLength: number;
  units: string;
  zZeroPosition: string;
  xyDatumPosition: string;
  toolSetter: string;
  probeForZHeightCommand: string;
  isDimensionalMaterial: boolean;
  dimensionalMaterialLinks?: string[];
  modelGroups?: IModelGroup[];
  currentPage: number;
  builtWith: IBuiltWith | '';
  // When true, explicitly submits the project for approval (transitions
  // visibility + sends the approval email exactly once). Omitted/false for
  // ordinary progress saves, which persist data only.
  publish?: boolean;
  // user: IUser;
  // visibility: string;

  // createdAt: string;
  // toolData: IToolData[];
  // jobSize: IJobSize;
  // likes: number;
  // hasLikedByUser: boolean;
  // comments: number;
  // attachments: IPostAttachment[];
};

export interface IPostAutoComplete {
  _id: string;
  title: string;
}

export interface IFileUploadQueue {
  preview: string;
  parentId: string;
  parentType: AttachmentParentType;
  category: IAttachmentCategory;
  fileName: string;
  fileHash: string;
  fileSize: number;
  resourceType: AttachmentType;
  controller?: string;
}
